/**
 * useFitnessScore — 获取 Financial Fitness 总分 + 三维数据
 *
 * API: GET /v2/agents/preferences → fitnessScore
 * 数据结构: { value, summary, modified, dimensions[{ code, value, summary }] }
 */

import type {
  FitnessScoreData,
  FitnessDimensionType,
} from "@components/fitness/types";
import { fitnessQueryKeys } from "./queryKeys";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { useQuery } from "@tanstack/react-query";

const DEFAULT_DIMENSION_LABEL: Record<FitnessDimensionType, string> = {
  control: "Control",
  safety: "Safety",
  growth: "Growth",
  fluency: "Fluency",
  peace: "Peace",
};

const DEFAULT_DIMENSION_SUBTITLE: Record<FitnessDimensionType, string> = {
  control: "Your monthly cash flow",
  safety: "Your cushion if a shock hits",
  growth: "Is your money building",
  fluency: "How smoothly your money works",
  peace: "How calm your financial life feels",
};

function normalizeDimensionLabel(
  code: FitnessDimensionType,
  label: unknown,
): string {
  if (typeof label !== "string" || label.trim().length === 0) {
    return DEFAULT_DIMENSION_LABEL[code];
  }
  const normalized = label.trim();
  if (normalized.toLowerCase() === code) {
    return DEFAULT_DIMENSION_LABEL[code];
  }
  return normalized;
}

function isFitnessDimensionType(value: unknown): value is FitnessDimensionType {
  return (
    value === "control" ||
    value === "safety" ||
    value === "growth" ||
    value === "fluency" ||
    value === "peace"
  );
}

function normalizeFitnessScore(raw: unknown): FitnessScoreData | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const score = raw as {
    value?: unknown;
    summary?: unknown;
    modified?: unknown;
    dimensions?: unknown;
  };

  if (typeof score.value !== "number" || !Number.isFinite(score.value)) {
    return null;
  }

  const dimensions = Array.isArray(score.dimensions) ? score.dimensions : [];
  const normalizedDimensions = dimensions
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const dim = item as {
        code?: unknown;
        value?: unknown;
        summary?: unknown;
        label?: unknown;
        subtitle?: unknown;
      };
      if (!isFitnessDimensionType(dim.code)) {
        return null;
      }
      if (typeof dim.value !== "number" || !Number.isFinite(dim.value)) {
        return null;
      }
      return {
        code: dim.code,
        value: dim.value,
        summary: typeof dim.summary === "string" ? dim.summary : "",
        label: normalizeDimensionLabel(dim.code, dim.label),
        subtitle:
          typeof dim.subtitle === "string" && dim.subtitle.trim().length > 0
            ? dim.subtitle
            : DEFAULT_DIMENSION_SUBTITLE[dim.code],
      };
    })
    .filter((item): item is FitnessScoreData["dimensions"][number] => !!item);

  return {
    value: Math.round(score.value),
    summary: typeof score.summary === "string" ? score.summary : "",
    modified:
      typeof score.modified === "number" && Number.isFinite(score.modified)
        ? score.modified
        : null,
    dimensions: normalizedDimensions,
  };
}

async function fetchScore(
  memberApi: ReturnType<typeof useOspApiClientProvider>["memberApi"],
): Promise<FitnessScoreData | null> {
  try {
    const res = await memberApi?.agents.getPreference();
    const pref = res?.data?.data as Record<string, unknown> | undefined;
    const raw = pref?.fitness_score ?? pref?.fitnessScore;
    return normalizeFitnessScore(raw);
  } catch {
    return null;
  }
}

export function useFitnessScore() {
  const { memberApi } = useOspApiClientProvider();

  const { data, isLoading, isPending, isFetching, error, refetch } = useQuery({
    queryKey: fitnessQueryKeys.score(),
    enabled: !!memberApi,
    queryFn: () => fetchScore(memberApi),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  return {
    data: data ?? null,
    isLoading,
    isPending,
    isFetching,
    error,
    refetch,
  };
}
